import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  LocalShipping as TruckIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  FilterList as FilterIcon,
  Assignment as AssignIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { getAllDeliveries, getDeliveryStats, assignDriver, getAllUsers, createDelivery, getAllSofas, updateDelivery, deleteDelivery, acceptDelivery, startDelivery, arriveDelivery, completeDelivery } from '../api';
import { can } from '../capabilities';

// Статусы доставки с метаданными
const DELIVERY_STATUSES = {
  pending: { label: 'Ожидает', color: 'default', icon: <ScheduleIcon /> },
  ready: { label: 'Готов', color: 'info', icon: <TruckIcon /> },
  assigned: { label: 'Назначен', color: 'primary', icon: <PersonIcon /> },
  accepted: { label: 'Принят', color: 'secondary', icon: <CheckIcon /> },
  in_transit: { label: 'В пути', color: 'warning', icon: <TruckIcon /> },
  arrived: { label: 'На месте', color: 'info', icon: <HomeIcon /> },
  delivered: { label: 'Доставлен', color: 'success', icon: <CheckIcon /> },
  problem: { label: 'Проблема', color: 'error', icon: <WarningIcon /> },
  cancelled: { label: 'Отменён', color: 'default', icon: <WarningIcon /> },
};

// Компонент статистики
function DeliveryStatsCards({ stats }) {
  if (!stats) return null;
  
  const cards = [
    { label: 'Всего', value: stats.total, color: '#1e40af' },
    { label: 'Ожидают', value: stats.pending + stats.ready, color: '#6b7280' },
    { label: 'В работе', value: stats.assigned + stats.in_transit, color: '#475569' },
    { label: 'Доставлено', value: stats.delivered, color: '#059669' },
    { label: 'Проблемы', value: stats.problems, color: '#374151' },
    { label: 'Сегодня', value: `${stats.today_completed}/${stats.today_deliveries}`, color: '#3b82f6' },
  ];
  
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card, index) => (
        <Grid item xs={6} sm={4} md={2} key={index}>
          <Card sx={{ borderLeft: `4px solid ${card.color}` }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: card.color }}>
                {card.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

// Диалог назначения водителя
function AssignDriverDialog({ open, onClose, delivery, drivers, onAssign }) {
  const [driverId, setDriverId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [timeSlotStart, setTimeSlotStart] = useState('');
  const [timeSlotEnd, setTimeSlotEnd] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && delivery) {
      setDriverId(delivery.driver_id || '');
      setScheduledDate(delivery.scheduled_date?.slice(0, 10) || '');
      setTimeSlotStart(delivery.time_slot_start || '');
      setTimeSlotEnd(delivery.time_slot_end || '');
    }
  }, [open, delivery]);

  const handleAssign = async () => {
    if (!driverId) return;
    setLoading(true);
    try {
      await onAssign(delivery.id, {
        driver_id: parseInt(driverId),
        scheduled_date: scheduledDate || null,
        time_slot_start: timeSlotStart || null,
        time_slot_end: timeSlotEnd || null,
      });
      onClose();
    } catch (error) {
      console.error('Error assigning driver:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}>
      <DialogTitle>
        Назначить водителя
        {delivery && ` - Доставка #${delivery.id}`}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Водитель</InputLabel>
              <Select
                value={driverId}
                label="Водитель"
                onChange={(e) => setDriverId(e.target.value)}
              >
                {drivers.map((driver) => (
                  <MenuItem key={driver.id} value={driver.id}>
                    {driver.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="date"
              label="Дата доставки"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="time"
              label="Время с"
              value={timeSlotStart}
              onChange={(e) => setTimeSlotStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="time"
              label="Время до"
              value={timeSlotEnd}
              onChange={(e) => setTimeSlotEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!driverId || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AssignIcon />}
        >
          Назначить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Диалог создания доставки
function CreateDeliveryDialog({ open, onClose, sofas, drivers = [], onCreate }) {
  const [formData, setFormData] = useState({
    sofa_id: '',
    // Данные клиента
    customer_name: '',
    customer_phone: '',
    customer_phone2: '',
    // Адрес доставки
    city: '',
    street: '',
    building: '',
    entrance: '',
    floor: '',
    apartment: '',
    intercom: '',
    has_elevator: false,
    // Назначение водителя (опционально)
    driver_id: '',
    scheduled_date: '',
    time_slot_start: '',
    time_slot_end: '',
    // Дополнительно
    lift_payment: 0,
    assembly_payment: 0,
    requires_assembly: false,
    priority: 3,
    comment: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Автоподстановка даты доставки из дивана при выборе
      if (field === 'sofa_id') {
        const sofa = sofas.find((s) => s.id === value);
        if (sofa && sofa.delivery_date && !prev.scheduled_date) {
          next.scheduled_date = sofa.delivery_date.slice(0, 10);
        }
      }
      return next;
    });
  };

  // Собираем полный адрес из компонентов
  const buildFullAddress = () => {
    const parts = [];
    if (formData.street) parts.push(formData.street);
    if (formData.building) parts.push(`д. ${formData.building}`);
    if (formData.apartment) parts.push(`кв. ${formData.apartment}`);
    return parts.join(', ');
  };

  const handleCreate = async () => {
    // Валидация обязательных полей
    if (!formData.sofa_id) {
      alert('Выберите диван');
      return;
    }
    if (!formData.customer_name?.trim()) {
      alert('Укажите имя клиента');
      return;
    }
    if (!formData.customer_phone?.trim()) {
      alert('Укажите номер телефона клиента');
      return;
    }
    if (!formData.city?.trim() && !formData.street?.trim()) {
      alert('Укажите адрес доставки (город или улица)');
      return;
    }
    setLoading(true);
    try {
      // Формируем данные для отправки
      const submitData = {
        sofa_id: formData.sofa_id,
        customer_name: formData.customer_name,
        phone: formData.customer_phone,
        customer_phone: formData.customer_phone,
        customer_phone2: formData.customer_phone2,
        address: buildFullAddress(),
        city: formData.city,
        entrance: formData.entrance,
        floor: formData.floor,
        apartment: formData.apartment,
        intercom: formData.intercom,
        has_elevator: formData.has_elevator,
        lift_payment: formData.lift_payment,
        assembly_payment: formData.assembly_payment,
        requires_assembly: formData.requires_assembly,
        priority: formData.priority,
        comment: formData.comment,
        // Назначение водителя (если выбрано)
        driver_id: formData.driver_id ? parseInt(formData.driver_id) : null,
        scheduled_date: formData.scheduled_date || null,
        time_slot_start: formData.time_slot_start || null,
        time_slot_end: formData.time_slot_end || null,
      };
      await onCreate(submitData);
      onClose();
      setFormData({
        sofa_id: '',
        customer_name: '',
        customer_phone: '',
        customer_phone2: '',
        city: '',
        street: '',
        building: '',
        entrance: '',
        floor: '',
        apartment: '',
        intercom: '',
        has_elevator: false,
        driver_id: '',
        scheduled_date: '',
        time_slot_start: '',
        time_slot_end: '',
        lift_payment: 0,
        assembly_payment: 0,
        requires_assembly: false,
        priority: 3,
        comment: '',
      });
    } catch (error) {
      console.error('Error creating delivery:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}>
      <DialogTitle>Создать доставку</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Выбор дивана */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              📦 Товар
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Диван</InputLabel>
              <Select
                value={formData.sofa_id}
                label="Диван"
                onChange={handleChange('sofa_id')}
              >
                {sofas.map((sofa) => (
                  <MenuItem key={sofa.id} value={sofa.id}>
                    #{sofa.number} - {sofa.model_name || 'Без модели'} {sofa.fabric_name ? `(${sofa.fabric_name})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Данные клиента */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
              👤 Данные клиента
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Имя клиента"
              value={formData.customer_name}
              onChange={handleChange('customer_name')}
              required
              placeholder="Иван Иванов"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Телефон"
              value={formData.customer_phone}
              onChange={handleChange('customer_phone')}
              required
              placeholder="+7 (999) 123-45-67"
              InputProps={{
                startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Доп. телефон"
              value={formData.customer_phone2}
              onChange={handleChange('customer_phone2')}
              placeholder="+7 (999) 765-43-21"
            />
          </Grid>

          {/* Адрес доставки */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
              🏠 Адрес доставки
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Город"
              value={formData.city}
              onChange={handleChange('city')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Улица"
              value={formData.street}
              onChange={handleChange('street')}
              required
              placeholder="ул. Ленина"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Дом"
              value={formData.building}
              onChange={handleChange('building')}
              required
              placeholder="15А"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Подъезд"
              type="number"
              value={formData.entrance}
              onChange={handleChange('entrance')}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Этаж"
              type="number"
              value={formData.floor}
              onChange={handleChange('floor')}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Квартира"
              value={formData.apartment}
              onChange={handleChange('apartment')}
              placeholder="42"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Домофон"
              value={formData.intercom}
              onChange={handleChange('intercom')}
              placeholder="42#"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Лифт</InputLabel>
              <Select
                value={formData.has_elevator}
                label="Лифт"
                onChange={handleChange('has_elevator')}
              >
                <MenuItem value={false}>Нет</MenuItem>
                <MenuItem value={true}>Есть</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Оплата и дополнительно */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
              💰 Оплата и услуги
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="За подъём (₸)"
              type="number"
              value={formData.lift_payment}
              onChange={handleChange('lift_payment')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="За сборку (₸)"
              type="number"
              value={formData.assembly_payment}
              onChange={handleChange('assembly_payment')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Сборка</InputLabel>
              <Select
                value={formData.requires_assembly}
                label="Сборка"
                onChange={handleChange('requires_assembly')}
              >
                <MenuItem value={false}>Не нужна</MenuItem>
                <MenuItem value={true}>Нужна</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Приоритет</InputLabel>
              <Select
                value={formData.priority}
                label="Приоритет"
                onChange={handleChange('priority')}
              >
                <MenuItem value={1}>1 - Низкий</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3 - Обычный</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5 - Срочный</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Комментарий к доставке"
              multiline
              rows={2}
              value={formData.comment}
              onChange={handleChange('comment')}
              placeholder="Особые пожелания клиента, ориентиры..."
            />
          </Grid>

          {/* Назначение водителя (опционально) */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
              🚚 Назначение водителя (необязательно)
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Водитель</InputLabel>
              <Select
                value={formData.driver_id}
                label="Водитель"
                onChange={handleChange('driver_id')}
              >
                <MenuItem value=""><em>Не назначен</em></MenuItem>
                {drivers.map((driver) => (
                  <MenuItem key={driver.id} value={driver.id}>
                    {driver.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Дата доставки"
              value={formData.scheduled_date}
              onChange={handleChange('scheduled_date')}
              InputLabelProps={{ shrink: true }}
              helperText="Подставляется автоматически из плана дивана"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="time"
              label="Время с"
              value={formData.time_slot_start}
              onChange={handleChange('time_slot_start')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="time"
              label="Время до"
              value={formData.time_slot_end}
              onChange={handleChange('time_slot_end')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!formData.sofa_id || !formData.street || !formData.building || !formData.customer_phone || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          Создать доставку
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Диалог редактирования/дозаполнения доставки
function EditDeliveryDialog({ open, onClose, delivery, drivers, onSave }) {
  const [formData, setFormData] = useState({
    // Данные клиента
    customer_name: '',
    customer_phone: '',
    customer_phone2: '',
    // Адрес доставки
    city: '',
    street: '',
    building: '',
    entrance: '',
    floor: '',
    apartment: '',
    intercom: '',
    has_elevator: false,
    // Планирование
    driver_id: '',
    scheduled_date: '',
    time_slot_start: '',
    time_slot_end: '',
    // Оплата и услуги
    lift_payment: 0,
    assembly_payment: 0,
    requires_assembly: false,
    priority: 3,
    comment: '',
    internal_notes: '',
  });
  const [loading, setLoading] = useState(false);

  // Парсим адрес на компоненты при загрузке
  const parseAddress = (address) => {
    if (!address) return { street: '', building: '' };
    
    // Пытаемся выделить улицу и дом
    const patterns = [
      /^(.+?),?\s*д\.?\s*(\S+)/, // "ул. Ленина, д. 15" или "ул. Ленина д.15"
      /^(.+?)\s+(\d+\S*)$/, // "ул. Ленина 15А"
    ];
    
    for (const pattern of patterns) {
      const match = address.match(pattern);
      if (match) {
        return { street: match[1].trim(), building: match[2].trim() };
      }
    }
    
    return { street: address, building: '' };
  };

  // Заполняем форму данными доставки при открытии
  useEffect(() => {
    if (delivery && open) {
      const { street, building } = parseAddress(delivery.address);
      
      setFormData({
        customer_name: delivery.customer_name || '',
        customer_phone: delivery.customer_phone || delivery.phone || '',
        customer_phone2: delivery.customer_phone2 || '',
        city: delivery.city || '',
        street: street,
        building: building,
        entrance: delivery.entrance || '',
        floor: delivery.floor || '',
        apartment: delivery.apartment || '',
        intercom: delivery.intercom || '',
        has_elevator: delivery.has_elevator || false,
        driver_id: delivery.driver_id || '',
        scheduled_date: delivery.scheduled_date?.slice(0, 10) || '',
        time_slot_start: delivery.time_slot_start || '',
        time_slot_end: delivery.time_slot_end || '',
        lift_payment: delivery.lift_payment || 0,
        assembly_payment: delivery.assembly_payment || 0,
        requires_assembly: delivery.requires_assembly || false,
        priority: delivery.priority || 3,
        comment: delivery.comment || '',
        internal_notes: delivery.internal_notes || '',
      });
    }
  }, [delivery, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
  };

  // Собираем полный адрес из компонентов
  const buildFullAddress = () => {
    const parts = [];
    if (formData.street) parts.push(formData.street);
    if (formData.building) parts.push(`д. ${formData.building}`);
    if (formData.apartment) parts.push(`кв. ${formData.apartment}`);
    return parts.join(', ');
  };

  // Проверяем заполненность ключевых полей
  const getCompleteness = () => {
    const fields = {
      customer_name: !!formData.customer_name,
      customer_phone: !!formData.customer_phone,
      street: !!formData.street,
      building: !!formData.building,
      city: !!formData.city,
    };
    const filled = Object.values(fields).filter(Boolean).length;
    return { filled, total: Object.keys(fields).length, fields };
  };

  const completeness = getCompleteness();
  const isComplete = completeness.filled === completeness.total;

  const handleSave = async () => {
    setLoading(true);
    try {
      const submitData = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_phone2: formData.customer_phone2,
        address: buildFullAddress(),
        city: formData.city,
        entrance: formData.entrance ? parseInt(formData.entrance) : null,
        floor: formData.floor ? parseInt(formData.floor) : null,
        apartment: formData.apartment,
        intercom: formData.intercom,
        has_elevator: formData.has_elevator,
        driver_id: formData.driver_id ? parseInt(formData.driver_id) : null,
        scheduled_date: formData.scheduled_date || null,
        time_slot_start: formData.time_slot_start || null,
        time_slot_end: formData.time_slot_end || null,
        lift_payment: parseFloat(formData.lift_payment) || 0,
        assembly_payment: parseFloat(formData.assembly_payment) || 0,
        requires_assembly: formData.requires_assembly,
        priority: parseInt(formData.priority),
        comment: formData.comment,
        internal_notes: formData.internal_notes,
      };
      await onSave(delivery.id, submitData);
      onClose();
    } catch (error) {
      console.error('Error saving delivery:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!delivery) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}>
          <Box>
            <Typography variant="h6">
              ✏️ Редактировать доставку #{delivery.id}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Диван: {delivery.sofa_number || `#${delivery.sofa_id}`} {delivery.sofa_model && `- ${delivery.sofa_model}`}
              {delivery.fabric_name && ` | Ткань: ${delivery.fabric_name}`}
              {delivery.sewer_name && ` | Швея: ${delivery.sewer_name}`}
              {delivery.upholsterer_name && ` | Обивщик: ${delivery.upholsterer_name}`}
            </Typography>
          </Box>
          <Chip 
            label={isComplete ? 'Данные заполнены' : `Заполнено ${completeness.filled}/${completeness.total}`}
            color={isComplete ? 'success' : 'warning'}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Данные клиента */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              👤 Данные клиента
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Имя клиента"
              value={formData.customer_name}
              onChange={handleChange('customer_name')}
              required
              error={!formData.customer_name}
              helperText={!formData.customer_name ? 'Обязательное поле' : ''}
              placeholder="Иван Иванов"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Телефон"
              value={formData.customer_phone}
              onChange={handleChange('customer_phone')}
              required
              error={!formData.customer_phone}
              helperText={!formData.customer_phone ? 'Обязательное поле' : ''}
              placeholder="+7 (999) 123-45-67"
              InputProps={{
                startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Доп. телефон"
              value={formData.customer_phone2}
              onChange={handleChange('customer_phone2')}
              placeholder="+7 (999) 765-43-21"
            />
          </Grid>

          {/* Адрес доставки */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
              🏠 Адрес доставки
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Город"
              value={formData.city}
              onChange={handleChange('city')}
              required
              error={!formData.city}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Улица"
              value={formData.street}
              onChange={handleChange('street')}
              required
              error={!formData.street}
              helperText={!formData.street ? 'Обязательное поле' : ''}
              placeholder="ул. Ленина"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Дом"
              value={formData.building}
              onChange={handleChange('building')}
              required
              error={!formData.building}
              placeholder="15А"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Подъезд"
              type="number"
              value={formData.entrance}
              onChange={handleChange('entrance')}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Этаж"
              type="number"
              value={formData.floor}
              onChange={handleChange('floor')}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Квартира"
              value={formData.apartment}
              onChange={handleChange('apartment')}
              placeholder="42"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              label="Домофон"
              value={formData.intercom}
              onChange={handleChange('intercom')}
              placeholder="42#"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Лифт</InputLabel>
              <Select
                value={formData.has_elevator}
                label="Лифт"
                onChange={handleChange('has_elevator')}
              >
                <MenuItem value={false}>Нет</MenuItem>
                <MenuItem value={true}>Есть</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Планирование доставки */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
              📅 Планирование доставки
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              label="Дата доставки"
              value={formData.scheduled_date}
              onChange={handleChange('scheduled_date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2.5}>
            <TextField
              fullWidth
              type="time"
              label="Время с"
              value={formData.time_slot_start}
              onChange={handleChange('time_slot_start')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2.5}>
            <TextField
              fullWidth
              type="time"
              label="Время до"
              value={formData.time_slot_end}
              onChange={handleChange('time_slot_end')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Оплата и услуги */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
              💰 Оплата и услуги
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="За подъём (₸)"
              type="number"
              value={formData.lift_payment}
              onChange={handleChange('lift_payment')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="За сборку (₸)"
              type="number"
              value={formData.assembly_payment}
              onChange={handleChange('assembly_payment')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Сборка</InputLabel>
              <Select
                value={formData.requires_assembly}
                label="Сборка"
                onChange={handleChange('requires_assembly')}
              >
                <MenuItem value={false}>Не нужна</MenuItem>
                <MenuItem value={true}>Нужна</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Приоритет</InputLabel>
              <Select
                value={formData.priority}
                label="Приоритет"
                onChange={handleChange('priority')}
              >
                <MenuItem value={1}>1 - Низкий</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3 - Обычный</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5 - Срочный</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Комментарии */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
              📝 Комментарии
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Комментарий для водителя"
              multiline
              rows={2}
              value={formData.comment}
              onChange={handleChange('comment')}
              placeholder="Особые пожелания клиента, ориентиры..."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Внутренние заметки"
              multiline
              rows={2}
              value={formData.internal_notes}
              onChange={handleChange('internal_notes')}
              placeholder="Заметки для менеджеров..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          color={isComplete ? 'success' : 'primary'}
        >
          {isComplete ? 'Сохранить' : 'Сохранить (неполные данные)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DeliveriesList({ user }) {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [sofas, setSofas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isAdmin = can(user, 'manageDeliveries');
  const canDelete = can(user, 'deleteDelivery');
  const isDriver = user?.role === 'driver';
  const loadGeneration = useRef(0);
  const loadController = useRef(null);

  const loadData = useCallback(async () => {
    const generation = ++loadGeneration.current;
    loadController.current?.abort();
    const controller = new AbortController();
    loadController.current = controller;
    setLoading(true);
    try {
      const [deliveriesRes, statsRes] = await Promise.all([
        getAllDeliveries({ status: statusFilter || undefined }, { signal: controller.signal }),
        isAdmin ? getDeliveryStats() : Promise.resolve({ data: null }),
      ]);
      if (generation !== loadGeneration.current) return;
      setDeliveries(deliveriesRes.data);
      setStats(statsRes.data);

      if (isAdmin) {
        const [driversRes, sofasRes] = await Promise.all([
          getAllUsers({ role: 'driver' }),
          getAllSofas(),
        ]);
        if (generation !== loadGeneration.current) return;
        setDrivers(driversRes.data.filter(u => u.role === 'driver'));
        setSofas(sofasRes.data.filter(s => !s.delivery_id)); // Диваны без доставки
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED' || generation !== loadGeneration.current) return;
      console.error('Error loading data:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки данных', severity: 'error' });
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [statusFilter, isAdmin]);

  useEffect(() => {
    loadData();
    return () => loadController.current?.abort();
  }, [loadData]);

  const handleAssignDriver = async (deliveryId, assignData) => {
    try {
      await assignDriver(deliveryId, assignData);
      setSnackbar({ open: true, message: 'Водитель назначен', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка назначения водителя', severity: 'error' });
      throw error;
    }
  };

  const handleCreateDelivery = async (data) => {
    try {
      await createDelivery(data);
      setSnackbar({ open: true, message: 'Доставка создана', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка создания доставки', severity: 'error' });
      throw error;
    }
  };

  const handleUpdateDelivery = async (deliveryId, data) => {
    try {
      await updateDelivery(deliveryId, data);
      setSnackbar({ open: true, message: 'Доставка обновлена', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка обновления доставки', severity: 'error' });
      throw error;
    }
  };

  // Действия водителя
  const handleAcceptDelivery = async (deliveryId) => {
    try {
      await acceptDelivery(deliveryId);
      setSnackbar({ open: true, message: 'Доставка принята', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.detail || 'Ошибка', severity: 'error' });
    }
  };

  const handleStartDelivery = async (deliveryId) => {
    try {
      await startDelivery(deliveryId);
      setSnackbar({ open: true, message: 'Доставка начата — в пути', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.detail || 'Ошибка', severity: 'error' });
    }
  };

  const handleArriveDelivery = async (deliveryId) => {
    try {
      await arriveDelivery(deliveryId);
      setSnackbar({ open: true, message: 'Прибытие отмечено', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.detail || 'Ошибка', severity: 'error' });
    }
  };

  const handleCompleteDelivery = async (deliveryId) => {
    if (deliveries.find(delivery => delivery.id === deliveryId)?.status !== 'arrived') return;
    try {
      await completeDelivery(deliveryId, { notes: 'Доставлено' });
      setSnackbar({ open: true, message: 'Доставка завершена!', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.detail || 'Ошибка', severity: 'error' });
    }
  };

  const openAssignDialog = (delivery) => {
    setSelectedDelivery(delivery);
    setAssignDialogOpen(true);
  };

  const openEditDialog = (delivery) => {
    setSelectedDelivery(delivery);
    setEditDialogOpen(true);
  };

  const getStatusInfo = (status) => {
    return DELIVERY_STATUSES[status] || { label: status, color: 'default' };
  };

  const filterDeliveriesByTab = (deliveries) => {
    switch (tabValue) {
      case 0: // Все
        return deliveries;
      case 1: // Активные
        return deliveries.filter(d => 
          ['pending', 'ready', 'assigned', 'accepted', 'in_transit', 'arrived'].includes(d.status)
        );
      case 2: // Выполненные
        return deliveries.filter(d => d.status === 'delivered');
      case 3: // Проблемы
        return deliveries.filter(d => d.status === 'problem');
      default:
        return deliveries;
    }
  };

  const filteredDeliveries = filterDeliveriesByTab(deliveries);

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
        <Typography variant="h4">
          🚚 Доставки
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '& .MuiButton-root': { flex: { xs: 1, sm: 'none' } } }}>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Создать доставку
            </Button>
          )}
          <Tooltip title="Обновить">
            <IconButton aria-label="Обновить доставки" onClick={loadData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Статистика */}
      <DeliveryStatsCards stats={stats} />

      {/* Табы */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" aria-label="Фильтр доставок">
          <Tab label={`Все (${deliveries.length})`} />
          <Tab 
            label={
              <Badge badgeContent={stats?.assigned + stats?.in_transit || 0} color="warning">
                Активные
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={stats?.delivered || 0} color="success">
                Выполненные
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={stats?.problems || 0} color="error">
                Проблемы
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {/* Фильтр по статусу */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FilterIcon color="action" />
        <FormControl size="small" sx={{ minWidth: 0, width: { xs: '100%', sm: 200 } }}>
          <InputLabel>Статус</InputLabel>
          <Select
            value={statusFilter}
            label="Статус"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">Все статусы</MenuItem>
            {Object.entries(DELIVERY_STATUSES).map(([key, { label }]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Таблица */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>ID</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Диван / Ткань</TableCell>
                <TableCell>Адрес</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Клиент</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Телефон</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Оплата</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDeliveries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Нет доставок
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDeliveries.map((delivery) => {
                  const statusInfo = getStatusInfo(delivery.status);
                  return (
                    <TableRow 
                      key={delivery.id}
                      sx={{ 
                        '&:hover': { backgroundColor: 'action.hover' },
                        backgroundColor: delivery.priority >= 4 ? 'warning.lighter' : 'inherit'
                      }}
                    >
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" fontWeight="bold">
                          #{delivery.id}
                        </Typography>
                        {delivery.priority >= 4 && (
                          <Chip label="Срочно" size="small" color="error" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {delivery.sofa_number || `#${delivery.sofa_id}`}
                        {delivery.sofa_model && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {delivery.sofa_model}
                          </Typography>
                        )}
                        {delivery.fabric_name && (
                          <Chip label={delivery.fabric_name} size="small" variant="outlined" color="primary" sx={{ mt: 0.5, fontSize: '0.65rem' }} />
                        )}
                        {delivery.sewer_name && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Швея: {delivery.sewer_name}
                          </Typography>
                        )}
                        {delivery.upholsterer_name && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Обивщик: {delivery.upholsterer_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <HomeIcon fontSize="small" color="action" />
                          {delivery.address}
                        </Box>
                        {delivery.city && (
                          <Typography variant="caption" color="text.secondary">
                            {delivery.city}
                          </Typography>
                        )}
                        {delivery.floor && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Этаж: {delivery.floor} {delivery.has_elevator ? '(лифт)' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {delivery.customer_name ? (
                          <Typography variant="body2" fontWeight="medium">
                            {delivery.customer_name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {delivery.customer_phone || delivery.phone || '—'}
                          </Typography>
                        </Box>
                        {delivery.customer_phone2 && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {delivery.customer_phone2}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {delivery.scheduled_date ? (
                          <>
                            <Typography variant="body2">
                              {delivery.scheduled_date.slice(0, 10).split('-').reverse().join('.')}
                            </Typography>
                            {delivery.time_slot_start && (
                              <Typography variant="caption" color="text.secondary">
                                {delivery.time_slot_start} - {delivery.time_slot_end || '...'}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(delivery.lift_payment || 0) + (delivery.assembly_payment || 0)} ₸
                        </Typography>
                        {delivery.is_paid && (
                          <Chip label="Оплачено" size="small" color="success" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title="Открыть доставку">
                              <IconButton
                                aria-label={`Открыть доставку ${delivery.id}`}
                                size="small"
                                onClick={() => navigate(`/deliveries/${delivery.id}`)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {!['delivered', 'cancelled'].includes(delivery.status) && (
                              <Tooltip title={delivery.driver_id ? 'Переназначить водителя' : 'Назначить водителя'}>
                                <IconButton
                                  aria-label={`${delivery.driver_id ? 'Переназначить' : 'Назначить'} водителя для доставки ${delivery.id}`}
                                  size="small"
                                  color="primary"
                                  onClick={() => openAssignDialog(delivery)}
                                >
                                  <AssignIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Редактировать доставку">
                              <IconButton
                                aria-label={`Редактировать доставку ${delivery.id}`}
                                size="small"
                                color="primary"
                                onClick={() => openEditDialog(delivery)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            {canDelete && <Tooltip title="Удалить доставку">
                              <IconButton
                                aria-label={`Удалить доставку ${delivery.id}`}
                                size="small"
                                color="error"
                                onClick={async () => {
                                  if (window.confirm('Удалить доставку? Это действие необратимо.')) {
                                    try {
                                      await deleteDelivery(delivery.id);
                                      loadData();
                                    } catch (err) {
                                      alert('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
                                    }
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>}
                          </Box>
                        </TableCell>
                      )}
                      {isDriver && (
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button size="small" variant="outlined" onClick={() => navigate(`/deliveries/${delivery.id}`)} sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.7rem' }}>
                              Подробнее
                            </Button>
                            {delivery.status === 'assigned' && (
                              <Tooltip title="Принять доставку">
                                <Button size="small" variant="contained" color="primary" onClick={() => handleAcceptDelivery(delivery.id)} sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.7rem' }}>
                                  Принять
                                </Button>
                              </Tooltip>
                            )}
                            {delivery.status === 'accepted' && (
                              <Tooltip title="Выехал — начать доставку">
                                <Button size="small" variant="contained" color="warning" onClick={() => handleStartDelivery(delivery.id)} sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.7rem' }}>
                                  Выехал
                                </Button>
                              </Tooltip>
                            )}
                            {delivery.status === 'in_transit' && (
                              <Tooltip title="Прибыл на место">
                                <Button size="small" variant="contained" color="info" onClick={() => handleArriveDelivery(delivery.id)} sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.7rem' }}>
                                  На месте
                                </Button>
                              </Tooltip>
                            )}
                            {delivery.status === 'arrived' && (
                              <Tooltip title="Доставлено">
                                <Button size="small" variant="contained" color="success" onClick={() => handleCompleteDelivery(delivery.id)} sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.7rem' }}>
                                  Доставлено
                                </Button>
                              </Tooltip>
                            )}
                            {!['assigned', 'accepted', 'in_transit', 'arrived'].includes(delivery.status) && (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      {!isAdmin && !isDriver && (
                        <TableCell>—</TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Диалоги */}

      <AssignDriverDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        delivery={selectedDelivery}
        drivers={drivers}
        onAssign={handleAssignDriver}
      />

      <CreateDeliveryDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        sofas={sofas}
        drivers={drivers}
        onCreate={handleCreateDelivery}
      />

      <EditDeliveryDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        delivery={selectedDelivery}
        drivers={drivers}
        onSave={handleUpdateDelivery}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
